import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../../router/all_routes';
import type { ItemType } from './types';

export interface UpdateItemTypePayload {
  id: number;
  itemType: string;
  description: string;
  checklistTemplate?: string | null;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  itemType: ItemType | null;
  onChange: (t: ItemType) => void;
  onSave: (payload: UpdateItemTypePayload) => void;
  loading: boolean;
}

type FieldType = 'text' | 'number' | 'checkbox' | 'date' | 'textarea' | 'select';
interface FieldDef {
  name: string;
  type: FieldType;
  required: boolean;
  unit?: string;
  options?: string[];
  width?: string;
}

type FooterType = 'text' | 'date' | 'line' | 'sign';
interface FooterField { id: string; label: string; type: FooterType; }

interface TableDef {
  id: string;
  title?: string;
  rows: number;
  rowHeight?: string;
  fields: Array<{ id: string; data: FieldDef }>;
  values?: Record<string, any>;
}

type SavedSchemaV2 = {
  version: 2;
  title: string;
  tables: Array<{
    title?: string;
    rows: number;
    rowHeight?: string;
    fields: FieldDef[];
    values?: Record<string, any>;
  }>;
  metaBox: {
    show: boolean;
    fields: string[];
    values?: Record<string, string>;
  };
  detailsRow: {
    show: boolean;
    rightCols: number;
    values?: Record<string, any>;
  };
  footer: {
    columns: number;
    fields: Array<{ label: string; type: FooterType }>;
    values?: Record<string, any>;
  };
};

const uid = () => Math.random().toString(36).slice(2, 9);

const EditItemTypeModal: React.FC<Props> = ({ show, onHide, onSuccess, itemType, onChange, onSave, loading }) => {
  const navigate = useNavigate();
  const [hasTemplate, setHasTemplate] = useState<boolean>(false);
  const [templateJson, setTemplateJson] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('Maintenance Checklist');
  const [tables, setTables] = useState<TableDef[]>([]);
  const [showMetaBox, setShowMetaBox] = useState<boolean>(false);
  const [metaFields, setMetaFields] = useState<Array<{ id: string; label: string }>>([]);
  const [metaValues, setMetaValues] = useState<Record<string, string>>({});
  const [showDetailsRow, setShowDetailsRow] = useState<boolean>(false);
  const [footerCols, setFooterCols] = useState<number>(1);
  const [footerFields, setFooterFields] = useState<FooterField[]>([]);
  const [entryMode, setEntryMode] = useState<boolean>(true);
  const [tableValues, setTableValues] = useState<Record<string, any>>({});
  const [detailsValues, setDetailsValues] = useState<Record<string, any>>({});
  const [footerValues, setFooterValues] = useState<Record<string, any>>({});

  // Fetch item type data
  useEffect(() => {
    const fetchItemType = async () => {
      if (!itemType?.id) return;
      setIsLoading(true);
      try {
        const response = await apiDefinitions.getItemTypeById(itemType.id);
        if (response?.data?.status === 'S1000') {
          const data = response.data.data;
          onChange({
            id: data.id,
            itemType: data.itemType,
            description: data.description,
            checklistTemplate: data.checklistTemplate,
          });
          setHasTemplate(!!data.checklistTemplate);
          setTemplateJson(data.checklistTemplate || '');
        } else {
          showError(response?.data?.statusDescription || 'Failed to fetch item type');
        }
      } catch (error: any) {
        if (error?.response?.status === 401) {
          showError('Unauthorized: Invalid or expired token. Please log in again.');
          localStorage.removeItem('token');
          navigate(all_routes.login);
        } else {
          showError(error?.response?.data?.statusDescription || 'Error fetching item type');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchItemType();
  }, [itemType?.id, onChange, navigate]);

  // Initialize state from checklistTemplate
  const initialState = useMemo(() => {
    let title = itemType?.itemType || 'Maintenance Checklist';
    let tables: TableDef[] = [{
      id: uid(),
      title: '',
      rows: 14,
      fields: [],
      values: {}
    }];
    let metaBox: { show: boolean; fields: string[]; values: Record<string, string> } = {
      show: true,
      fields: ['Date', 'Inspector'],
      values: {}
    };
    let detailsRow = { show: true, rightCols: 0, values: {} };
    let footer: { columns: number; fields: FooterField[]; values: Record<string, any> } = {
      columns: 2,
      fields: [
        { id: uid(), label: 'Checked by', type: 'text' },
        { id: uid(), label: 'Approved by', type: 'text' },
      ],
      values: {}
    };
    let tempTableValues: Record<string, any> = {};
    let tempDetailsValues: Record<string, any> = {};

    if (templateJson?.trim()) {
      try {
        const parsed = JSON.parse(templateJson) as SavedSchemaV2;
        if (parsed?.version === 2) {
          const s = parsed;
          title = s.title ?? title;
          tables = Array.isArray(s.tables) ? s.tables.map((t, idx) => {
            const newTableId = uid();
            const newTable: TableDef = {
              id: newTableId,
              title: t.title || '',
              rows: Number.isFinite(t.rows) ? Math.max(5, Math.min(50, t.rows)) : 14,
              rowHeight: t.rowHeight || '40px',
              fields: Array.isArray(t.fields) ? t.fields.map((f, fi) => ({
                id: `field-${idx}-${fi}`,
                data: {
                  name: f.name || '',
                  type: f.type || 'text',
                  required: !!f.required,
                  unit: f.unit,
                  options: f.options,
                  width: f.width || 'auto'
                }
              })) : [],
              values: {}
            };
            // Remap table values
            const oldValues = t.values || {};
            const oldKeys = Object.keys(oldValues);
            if (oldKeys.length > 0) {
              const oldId = oldKeys[0]?.split(':')[0] || '';
              for (const key of oldKeys) {
                const newKey = key.replace(oldId, newTableId);
                tempTableValues[newKey] = oldValues[key];
              }
            }
            // Remap details values
            const oldDetailsValues = s.detailsRow?.values || {};
            for (const key in oldDetailsValues) {
              if (key.startsWith(`details:${oldKeys[0]?.split(':')[0] || ''}:`)) {
                const newKey = key.replace(`details:${oldKeys[0]?.split(':')[0] || ''}:`, `details:${newTableId}:`);
                tempDetailsValues[newKey] = oldDetailsValues[key];
              }
            }
            return newTable;
          }) : tables;

          metaBox = {
            show: !!s.metaBox?.show,
            fields: Array.isArray(s.metaBox?.fields) ? s.metaBox.fields : metaBox.fields,
            values: s.metaBox?.values || {}
          };

          detailsRow = {
            show: !!s.detailsRow?.show,
            rightCols: Number.isFinite(s.detailsRow?.rightCols) ? s.detailsRow.rightCols : 0,
            values: tempDetailsValues
          };

          footer = {
            columns: Math.max(1, Math.min(4, s.footer?.columns ?? 2)),
            fields: Array.isArray(s.footer?.fields) ? s.footer.fields.map((f, i) => ({
              id: Object.keys(s.footer?.values || {})[i] || uid(),
              label: f.label || '',
              type: f.type || 'text'
            })) : footer.fields,
            values: s.footer?.values || {}
          };
        }
      } catch (e) {
        console.error('Failed to parse checklistTemplate:', e);
        showError('Invalid JSON in checklistTemplate');
      }
    }

    return {
      title,
      tables,
      metaBox,
      detailsRow,
      footer,
      tempTableValues,
      tempDetailsValues
    };
  }, [templateJson, itemType?.itemType]);

  useEffect(() => {
    setTitle(initialState.title);
    setTables(initialState.tables);
    setShowMetaBox(initialState.metaBox.show);
    setMetaFields(initialState.metaBox.fields.map((f, i) => ({ id: `meta-${i}`, label: f })));
    setMetaValues(initialState.metaBox.values);
    setShowDetailsRow(initialState.detailsRow.show);
    setFooterCols(initialState.footer.columns);
    setFooterFields(initialState.footer.fields);
    setTableValues(initialState.tempTableValues);
    setDetailsValues(initialState.tempDetailsValues);
    setFooterValues(initialState.footer.values);
  }, [initialState]);

  const addTable = () => {
    setTables(prev => [...prev, { id: uid(), title: '', rows: 14, fields: [], values: {} }]);
  };

  const removeTable = (tableId: string) => {
    setTables(prev => prev.filter(t => t.id !== tableId));
    setTableValues(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (k.startsWith(`${tableId}:`)) delete next[k];
      });
      return next;
    });
    setDetailsValues(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (k.startsWith(`details:${tableId}:`)) delete next[k];
      });
      return next;
    });
  };

  const updateTableTitle = (tableId: string, title: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, title } : t));
  };

  const updateTableRows = (tableId: string, rows: number) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, rows } : t));
  };

  const updateTableRowHeight = (tableId: string, rowHeight: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, rowHeight } : t));
  };

  const addColumn = (tableId: string) => {
    setTables(prev => prev.map(t => t.id === tableId ? {
      ...t,
      fields: [...t.fields, { id: uid(), data: { name: `col_${t.fields.length + 1}`, type: 'text', required: false } }]
    } : t));
  };

  const updateColumn = (tableId: string, id: string, patch: Partial<FieldDef>) => {
    setTables(prev => prev.map(t => t.id === tableId ? {
      ...t,
      fields: t.fields.map(c => (c.id === id ? { ...c, data: { ...c.data, ...patch } } : c))
    } : t));
  };

  const removeColumn = (tableId: string, id: string) => {
    let colName: string | undefined;
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      colName = t.fields.find(c => c.id === id)?.data.name;
      return {
        ...t,
        fields: t.fields.filter(c => c.id !== id)
      };
    }));
    setTableValues(prev => {
      const next = { ...prev };
      if (colName) {
        Object.keys(next).forEach(k => {
          if (k.startsWith(`${tableId}:`) && k.endsWith(`:${colName}`)) delete next[k];
        });
      }
      return next;
    });
    setDetailsValues(prev => {
      const next = { ...prev };
      if (colName) {
        Object.keys(next).forEach(k => {
          if (k === `details:${tableId}:${colName}`) delete next[k];
        });
      }
      return next;
    });
  };

  const move = (tableId: string, id: string, dir: -1 | 1) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      const i = t.fields.findIndex(c => c.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= t.fields.length) return t;
      const next = [...t.fields];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...t, fields: next };
    }));
  };

  const duplicate = (tableId: string, id: string) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      const i = t.fields.findIndex(c => c.id === id);
      if (i < 0) return t;
      const src = t.fields[i];
      const copy = { id: uid(), data: { ...src.data, name: `${src.data.name}_copy` } };
      const next = [...t.fields];
      next.splice(i + 1, 0, copy);
      return { ...t, fields: next };
    }));
  };

  const addOption = (tableId: string, id: string, raw: string) => {
    const val = raw.trim();
    if (!val) return;
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        fields: t.fields.map(c => {
          if (c.id !== id) return c;
          const opts = c.data.options ?? [];
          if (opts.includes(val)) return c;
          return { ...c, data: { ...c.data, options: [...opts, val] } };
        })
      };
    }));
  };

  const removeOption = (tableId: string, id: string, opt: string) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        fields: t.fields.map(c => {
          if (c.id !== id) return c;
          const next = (c.data.options ?? []).filter(o => o !== opt);
          return { ...c, data: { ...c.data, options: next.length ? next : undefined } };
        })
      };
    }));
  };

  const addMeta = () => setMetaFields(p => [...p, { id: uid(), label: `Field ${p.length + 1}` }]);
  const removeMeta = (id: string) => {
    setMetaFields(p => p.filter(m => m.id !== id));
    setMetaValues(prev => {
      const next = { ...prev };
      const label = metaFields.find(m => m.id === id)?.label;
      if (label) delete next[label];
      return next;
    });
  };

  const addFooter = () => setFooterFields(p => [...p, { id: uid(), label: `Field ${p.length + 1}`, type: 'text' }]);
  const updateFooter = (id: string, patch: Partial<FooterField>) =>
    setFooterFields(p => p.map(f => (f.id === id ? { ...f, ...patch } : f)));
  const removeFooter = (id: string) => {
    setFooterFields(p => p.filter(f => f.id !== id));
    setFooterValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const canSaveTemplate = tables.every(t => {
    const names = t.fields.map(c => c.data.name.trim()).filter(Boolean);
    return t.fields.length > 0 && !t.fields.some(c => !c.data.name.trim()) && new Set(names).size === names.length;
  });

  const resetTemplateForm = () => {
    setTables([{ id: uid(), title: '', rows: 14, fields: [], values: {} }]);
    setTitle(itemType?.itemType || 'Maintenance Checklist');
    setShowMetaBox(true);
    setMetaFields([{ id: uid(), label: 'Date' }, { id: uid(), label: 'Inspector' }]);
    setMetaValues({});
    setShowDetailsRow(true);
    setFooterCols(2);
    setFooterFields([
      { id: uid(), label: 'Checked by', type: 'text' },
      { id: uid(), label: 'Approved by', type: 'text' }
    ]);
    setTableValues({});
    setDetailsValues({});
    setFooterValues({});
  };

  const handleSaveTemplate = () => {
    const schema: SavedSchemaV2 = {
      version: 2,
      title: (title || 'Maintenance Checklist').trim(),
      tables: tables.map(t => ({
        title: t.title?.trim(),
        rows: Math.max(5, Math.min(50, t.rows | 0)),
        rowHeight: t.rowHeight?.trim(),
        fields: t.fields.map(c => {
          const base: FieldDef = {
            name: c.data.name.trim(),
            type: c.data.type,
            required: !!c.data.required,
          };
          if (c.data.type === 'number' && c.data.unit) base.unit = c.data.unit;
          if (c.data.type === 'select' && c.data.options?.length) base.options = c.data.options;
          if (c.data.width?.trim()) base.width = c.data.width.trim();
          return base;
        }),
        values: Object.keys(tableValues)
          .filter(k => k.startsWith(`${t.id}:`))
          .reduce((acc, k) => ({ ...acc, [k]: tableValues[k] }), {})
      })),
      metaBox: {
        show: showMetaBox,
        fields: metaFields.map(m => (m.label || '').trim()).filter(Boolean),
        values: metaValues
      },
      detailsRow: {
        show: showDetailsRow,
        rightCols: 0,
        values: detailsValues
      },
      footer: {
        columns: footerCols,
        fields: footerFields.map(f => ({ label: (f.label || '').trim(), type: f.type })),
        values: footerValues
      },
    };
    try {
      setTemplateJson(JSON.stringify(schema));
      onChange({ ...itemType!, checklistTemplate: JSON.stringify(schema) });
    } catch (e) {
      console.error('Failed to stringify checklistTemplate:', e);
      showError('Failed to save template due to invalid data');
    }
  };

  const handleSave = async () => {
    if (hasTemplate && !templateJson.trim()) {
      showError('Please save template changes before proceeding');
      return;
    }
    const payload: UpdateItemTypePayload = {
      id: itemType?.id ?? 0,
      itemType: itemType?.itemType ?? '',
      description: itemType?.description ?? '',
      checklistTemplate: hasTemplate ? templateJson.trim() : null
    };
    try {
      await onSave(payload);
      showSuccess("Item type updated successfully");
      onSuccess();
      onHide();
      resetTemplateForm();
    } catch (error: any) {
      showError(error.message || 'Failed to save item type');
    }
  };

  const previewId = `table-preview-edit-item-type`;
  const sheetRef = useRef<HTMLDivElement>(null);
  const printPdf = () => window.print();

  useEffect(() => {
    const styleId = `adv-table-style-edit-item-type`;
    if (document.getElementById(styleId)) return;
    const s = document.createElement('style');
    s.id = styleId;
    s.innerHTML = `
      .tb-card { border:1px solid #e6e6e6; border-radius:12px; padding:12px; background:#fff; box-shadow:0 1px 1px rgba(0,0,0,.04); }
      .tb-card + .tb-card { margin-top:10px; }
      .tb-btn { padding:6px 10px; border:1px solid #ddd; background:#fafafa; border-radius:8px; }
      .tb-preview-wrap { width:100%; max-height: 70vh; overflow-y: auto; }
      .tb-a4 { width:100%; max-width: 190mm; background:#fff; box-shadow:0 0 0.5cm rgba(0,0,0,.2); padding:10mm; margin:0 auto; color:#222; transform: scale(0.9); transform-origin: top left; }
      .tb-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:10px; }
      .tb-title { font-weight:700; font-size:18px; }
      .tb-meta { border:1px solid #bbb; border-collapse:collapse; }
      .tb-meta th, .tb-meta td { border:1px solid #bbb; padding:4px 8px; font-size:12px; }
      .tb-meta th { background:#f6f6f6; text-align:left; }
      .tb-table { width:100%; border-collapse:collapse; table-layout: fixed; margin-bottom:10mm; }
      .tb-table th, .tb-table td { border:1px solid #999; padding:6px 8px; font-size:12px; vertical-align:top; }
      .tb-table th { background:#f3f3f3; text-align:left; }
      .tb-input, .tb-select, .tb-textarea { width:100%; border:1px solid #cfd3d8; border-radius:6px; padding:6px 8px; font-size:12px; }
      .tb-textarea { min-height:40px; resize:vertical; }
      .tb-line { height:18px; border-bottom:1px solid #bbb; }
      .tb-bigline { height:40px; border:1px dashed #bbb; border-radius:6px; }
      .tb-box { width:16px; height:16px; border:2px solid #666; border-radius:3px; display:inline-block; vertical-align:middle; }
      .tb-radio { width:14px; height:14px; border:2px solid #666; border-radius:50%; display:inline-block; }
      .tb-footgrid { display:grid; gap:8mm; margin-top:8mm; }
      .tb-foot-item .lbl { font-weight:600; margin-bottom:4px; font-size:12px; }
      .tb-foot-item .sign { height:24mm; border:1px dashed #cbd5e1; border-radius:6px; }
      .tb-foot-item .line { height:18px; border-bottom:1px solid #bbb; }
      .modal-xl { max-width: 95vw; }
      @media (max-width: 1200px) { .tb-a4 { transform: scale(0.8); } }
      @media (max-width: 992px) { .tb-a4 { transform: scale(0.7); } }
      @media print {
        @page { size: A4; margin: 10mm; }
        body * { visibility: hidden !important; }
        #${previewId}, #${previewId} * { visibility: visible !important; }
        #${previewId} { position:absolute; left:0; top:0; width:190mm; transform: scale(1); }
        .modal, .modal-backdrop { display:none !important; }
        .no-print { display:none !important; }
      }
    `;
    document.head.appendChild(s);
    return () => {
      document.getElementById(styleId)?.remove();
    };
  }, [previewId]);

  const cellKey = (tableId: string, r: number, cName: string) => `${tableId}:${r}:${cName}`;

  const renderEntryCell = (tableId: string, f: FieldDef, r: number, cId: string) => {
    const k = cellKey(tableId, r, f.name);
    const v = tableValues[k];
    const set = (val: any) => setTableValues(prev => ({ ...prev, [k]: val }));

    return (
      <>
        {f.type === 'text' && (
          <Form.Control className="tb-input" value={v ?? ''} onChange={(e) => set(e.target.value)} />
        )}
        {f.type === 'number' && (
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              className="tb-input"
              type="number"
              value={v ?? ''}
              onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
              style={{ maxWidth: '120px' }}
            />
            {f.unit && <span className="text-muted">{f.unit}</span>}
          </div>
        )}
        {f.type === 'date' && (
          <Form.Control className="tb-input" type="date" value={v ?? ''} onChange={(e) => set(e.target.value)} />
        )}
        {f.type === 'checkbox' && (
          <Form.Check className="ms-1">
            <Form.Check.Input
              id={`chk_${k}`}
              checked={!!v}
              onChange={(e) => set(e.target.checked)}
            />
            <Form.Check.Label htmlFor={`chk_${k}`}>Yes</Form.Check.Label>
          </Form.Check>
        )}
        {f.type === 'textarea' && (
          <Form.Control
            as="textarea"
            className="tb-textarea"
            value={v ?? ''}
            onChange={(e) => set(e.target.value)}
          />
        )}
        {f.type === 'select' && (
          <Form.Select className="tb-select" value={v ?? ''} onChange={(e) => set(e.target.value)}>
            <option value="" disabled>{f.required ? 'Select… (required)' : 'Select…'}</option>
            {(f.options ?? []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        )}
      </>
    );
  };

  const renderDetailsCell = (tableId: string, c: { id: string; data: FieldDef }, ci: number) => {
    const k = `details:${tableId}:${c.data.name}`;
    const v = detailsValues[k];
    const set = (val: any) => setDetailsValues(prev => ({ ...prev, [k]: val }));

    return (
      <td key={`details-${c.id}`} style={{ width: c.data.width || 'auto' }}>
        {entryMode ? (
          <Form.Control
            as="textarea"
            className="tb-textarea"
            value={v ?? ''}
            placeholder="Notes / Description"
            onChange={(e) => set(e.target.value)}
          />
        ) : (
          <div className="tb-bigline" />
        )}
      </td>
    );
  };

  const renderFooterField = (f: FooterField) => {
    const k = f.id;
    const v = footerValues[k];
    const set = (val: any) => setFooterValues(prev => ({ ...prev, [k]: val }));

    return (
      <div className="tb-foot-item" key={f.id}>
        <div className="lbl">{f.label}</div>
        {f.type === 'sign' && <div className="sign" />}
        {f.type === 'line' && <div className="line" />}
        {f.type === 'text' && (
          entryMode ? (
            <Form.Control className="tb-input" value={v ?? ''} onChange={(e) => set(e.target.value)} />
          ) : (
            <div className="line" />
          )
        )}
        {f.type === 'date' && (
          entryMode ? (
            <Form.Control className="tb-input" type="date" value={v ?? ''} onChange={(e) => set(e.target.value)} />
          ) : (
            <div className="line" />
          )
        )}
      </div>
    );
  };

  const renderBuilder = () => (
    <div className="mt-4">
      <h5 className="mb-3">Configure Service Checklist Template</h5>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Form.Check>
          <Form.Check.Input
            id="entryModeEdit"
            checked={entryMode}
            onChange={(e) => setEntryMode(e.target.checked)}
          />
          <Form.Check.Label className="ms-1" htmlFor="entryModeEdit">
            Entry mode
          </Form.Check.Label>
        </Form.Check>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={addTable}>
            <i className="ti ti-plus me-1" /> Add Table
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={printPdf}
            disabled={!tables.some(t => t.fields.length)}
          >
            <i className="ti ti-printer me-1" /> Download / Print
          </Button>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="tb-card">
            <div className="row g-3">
              <div className="col-8">
                <Form.Label>Document Title</Form.Label>
                <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="col-12 d-flex flex-wrap align-items-center gap-3">
                <Form.Check>
                  <Form.Check.Input
                    id="toggleMeta"
                    checked={showMetaBox}
                    onChange={(e) => setShowMetaBox(e.target.checked)}
                  />
                  <Form.Check.Label className="ms-1" htmlFor="toggleMeta">
                    Show top-right meta box
                  </Form.Check.Label>
                </Form.Check>
                <Form.Check>
                  <Form.Check.Input
                    id="toggleDetailsRow"
                    checked={showDetailsRow}
                    onChange={(e) => setShowDetailsRow(e.target.checked)}
                  />
                  <Form.Check.Label className="ms-1" htmlFor="toggleDetailsRow">
                    Show “details row” under header
                  </Form.Check.Label>
                </Form.Check>
              </div>
            </div>
          </div>

          {showMetaBox && (
            <div className="tb-card mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Meta box (top-right)</div>
                <Button variant="outline-primary" size="sm" onClick={addMeta}>
                  <i className="ti ti-plus me-1" /> Add Meta Field
                </Button>
              </div>
              {metaFields.length === 0 && <div className="text-muted">Add meta fields like Date, Inspector, Location…</div>}
              {metaFields.map((m) => (
                <div className="row g-2 align-items-center mb-2" key={m.id}>
                  <div className="col-9">
                    <Form.Control
                      value={m.label}
                      onChange={(e) => setMetaFields(p => p.map(x => x.id === m.id ? { ...x, label: e.target.value } : x))}
                      placeholder="Label"
                    />
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <Button variant="outline-danger" size="sm" onClick={() => removeMeta(m.id)}>
                      <i className="ti ti-trash" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tables.map((table, tableIdx) => (
            <div className="mt-3" key={table.id}>
              <div className="tb-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="fw-semibold">Table #{tableIdx + 1}</div>
                    <Form.Control
                      style={{ maxWidth: 200 }}
                      value={table.title || ''}
                      onChange={(e) => updateTableTitle(table.id, e.target.value)}
                      placeholder="Table Title (optional)"
                    />
                    <div className="d-flex align-items-center gap-2">
                      <Form.Label className="mb-0">Rows</Form.Label>
                      <Form.Control
                        type="number"
                        style={{ width: 80 }}
                        min={5}
                        max={50}
                        value={table.rows}
                        onChange={(e) => updateTableRows(table.id, Math.min(50, Math.max(5, parseInt(e.target.value || '14', 10))))}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Label className="mb-0">Row Height</Form.Label>
                      <Form.Control
                        style={{ width: 100 }}
                        value={table.rowHeight ?? ''}
                        onChange={(e) => updateTableRowHeight(table.id, e.target.value)}
                        placeholder="e.g., 50px"
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeTable(table.id)}
                      disabled={tables.length === 1}
                    >
                      <i className="ti ti-trash" /> Remove Table
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => addColumn(table.id)}>
                      <i className="ti ti-plus me-1" /> Add Column
                    </Button>
                  </div>
                </div>
                {!table.fields.length && <div className="alert alert-info">Add columns for the checklist table.</div>}
                {table.fields.some(c => !c.data.name.trim()) && <div className="alert alert-warning py-2">All column names are required.</div>}
                {(() => {
                  const names = table.fields.map(c => c.data.name.trim()).filter(Boolean);
                  return names.length > 0 && new Set(names).size !== names.length && <div className="alert alert-warning py-2">Column names must be unique.</div>;
                })()}

                {table.fields.map((c, idx) => (
                  <div className="tb-card" key={c.id}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold">Column #{idx + 1}</div>
                      <div className="d-flex gap-2">
                        <Button
                          className="tb-btn"
                          onClick={() => move(table.id, c.id, -1)}
                          disabled={idx === 0}
                          title="Move up"
                        >
                          ↑
                        </Button>
                        <Button
                          className="tb-btn"
                          onClick={() => move(table.id, c.id, 1)}
                          disabled={idx === table.fields.length - 1}
                          title="Move down"
                        >
                          ↓
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => duplicate(table.id, c.id)} title="Duplicate">
                          <i className="ti ti-copy" />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => removeColumn(table.id, c.id)} title="Remove">
                          <i className="ti ti-trash" />
                        </Button>
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <Form.Label>Header Name</Form.Label>
                        <Form.Control
                          value={c.data.name}
                          onChange={(e) => updateColumn(table.id, c.id, { name: e.target.value })}
                          placeholder="e.g., Ref No / Location / Date Tested"
                        />
                      </div>
                      <div className="col-md-3">
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                          value={c.data.type}
                          onChange={(e) => {
                            const t = e.target.value as FieldType;
                            const patch: Partial<FieldDef> = { type: t };
                            if (t !== 'number') patch.unit = undefined;
                            if (t !== 'select') patch.options = undefined;
                            updateColumn(table.id, c.id, patch);
                          }}
                        >
                          <option value="text">text</option>
                          <option value="number">number</option>
                          <option value="checkbox">checkbox</option>
                          <option value="date">date</option>
                          <option value="textarea">textarea</option>
                          <option value="select">select</option>
                        </Form.Select>
                      </div>
                      <div className="col-md-3">
                        <Form.Label>Width</Form.Label>
                        <Form.Control
                          value={c.data.width ?? ''}
                          onChange={(e) => updateColumn(table.id, c.id, { width: e.target.value })}
                          placeholder="e.g., 150px or 20%"
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <Form.Check>
                          <Form.Check.Input
                            checked={c.data.required}
                            onChange={(e) => updateColumn(table.id, c.id, { required: e.target.checked })}
                            id={`req-${c.id}`}
                          />
                          <Form.Check.Label className="ms-2" htmlFor={`req-${c.id}`}>
                            Required
                          </Form.Check.Label>
                        </Form.Check>
                      </div>
                      {c.data.type === 'number' && (
                        <div className="col-md-4">
                          <Form.Label>Unit (optional)</Form.Label>
                          <Form.Control
                            placeholder="e.g., °C, L, kg"
                            value={c.data.unit ?? ''}
                            onChange={(e) => updateColumn(table.id, c.id, { unit: e.target.value })}
                          />
                        </div>
                      )}
                      {c.data.type === 'select' && (
                        <div className="col-12">
                          <Form.Label>Options</Form.Label>
                          <Form.Control
                            placeholder="Type option and press Enter"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addOption(table.id, c.id, (e.target as HTMLInputElement).value);
                                (e.target as HTMLInputElement).value = '';
                              }
                            }}
                          />
                          <div className="mt-2">
                            {(c.data.options ?? []).map((opt) => (
                              <span key={opt} className="badge bg-light text-dark me-1">
                                {opt}{' '}
                                <button
                                  className="btn btn-link btn-sm p-0 ms-1"
                                  onClick={() => removeOption(table.id, c.id, opt)}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            {!(c.data.options ?? []).length && <div className="text-muted">No options yet.</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="tb-card mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Footer (customizable)</div>
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Columns</Form.Label>
                <Form.Control
                  type="number"
                  style={{ width: 80 }}
                  min={1}
                  max={4}
                  value={footerCols}
                  onChange={(e) => setFooterCols(Math.max(1, Math.min(4, parseInt(e.target.value || '2', 10))))}
                />
                <Button variant="outline-primary" size="sm" onClick={addFooter}>
                  <i className="ti ti-plus me-1" /> Add Footer Field
                </Button>
              </div>
            </div>
            {footerFields.length === 0 && <div className="text-muted">No footer fields yet.</div>}
            {footerFields.map(f => (
              <div className="row g-2 align-items-center mb-2" key={f.id}>
                <div className="col-5">
                  <Form.Control
                    value={f.label}
                    onChange={(e) => updateFooter(f.id, { label: e.target.value })}
                    placeholder="Label"
                  />
                </div>
                <div className="col-4">
                  <Form.Select
                    value={f.type}
                    onChange={(e) => updateFooter(f.id, { type: e.target.value as FooterType })}
                  >
                    <option value="text">Text (typeable)</option>
                    <option value="date">Date (typeable)</option>
                    <option value="line">Line (print line)</option>
                    <option value="sign">Signature box</option>
                  </Form.Select>
                </div>
                <div className="col-3 d-flex justify-content-end">
                  <Button variant="outline-danger" size="sm" onClick={() => removeFooter(f.id)}>
                    <i className="ti ti-trash" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="tb-preview-wrap" id={previewId}>
            <div className="tb-a4" ref={sheetRef}>
              <div className="tb-head">
                <div>
                  <div className="tb-title">{(title || 'Maintenance Checklist').trim()}</div>
                  <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
                    Generated: {new Date().toLocaleDateString()}
                  </div>
                </div>
                {showMetaBox && metaFields.length > 0 && (
                  <table className="tb-meta">
                    <thead><tr><th colSpan={2}>Details</th></tr></thead>
                    <tbody>
                      {metaFields.map((m) => (
                        <tr key={m.id}>
                          <td style={{ minWidth: 90 }}>{m.label}</td>
                          <td style={{ minWidth: 160 }}>
                            {entryMode ? (
                              <Form.Control
                                className="tb-input"
                                value={metaValues[m.label] ?? ''}
                                onChange={(e) => setMetaValues(prev => ({ ...prev, [m.label]: e.target.value }))}
                              />
                            ) : (
                              <div className="tb-line" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {tables.map((table, tableIdx) => (
                <div key={table.id}>
                  <div className="fw-semibold mb-2">{table.title?.trim() || `Table #${tableIdx + 1}`}</div>
                  <table className="tb-table">
                    <thead>
                      <tr style={{ height: table.rowHeight || '40px' }}>
                        {table.fields.map((c) => (
                          <th key={c.id} style={{ width: c.data.width || 'auto' }}>
                            {c.data.name || '—'}
                            {c.data.required && <span style={{ color: '#d32f2f', marginLeft: 6 }}>*</span>}
                            {c.data.type === 'number' && c.data.unit && (
                              <span style={{ marginLeft: 6, fontWeight: 400, color: '#666' }}>({c.data.unit})</span>
                            )}
                          </th>
                        ))}
                        {!table.fields.length && <th>Define columns on the left…</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {showDetailsRow && table.fields.length > 0 && (
                        <tr style={{ height: table.rowHeight || '40px' }}>
                          {table.fields.map((c, ci) => renderDetailsCell(table.id, c, ci))}
                        </tr>
                      )}
                      {Array.from({ length: table.rows }).map((_, r) => (
                        <tr key={`r-${r}`} style={{ height: table.rowHeight || '40px' }}>
                          {table.fields.map((c) => (
                            <td key={c.id} style={{ width: c.data.width || 'auto' }}>
                              {entryMode ? renderEntryCell(table.id, c.data, r, c.id) : (
                                c.data.type === 'textarea' ? <div className="tb-bigline" /> :
                                c.data.type === 'checkbox' ? <span className="tb-box" /> :
                                c.data.type === 'select' ? (
                                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    {(c.data.options?.slice(0, 4) || ['Option 1', 'Option 2']).map((o) => (
                                      <span key={o} style={{ whiteSpace: 'nowrap' }}>
                                        <span className="tb-radio" /> {o}
                                      </span>
                                    ))}
                                  </div>
                                ) : <div className="tb-line" />
                              )}
                            </td>
                          ))}
                          {!table.fields.length && <td />}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {footerCols > 0 && footerFields.length > 0 && (
                <div className="tb-footgrid" style={{ gridTemplateColumns: `repeat(${footerCols}, 1fr)` }}>
                  {footerFields.map(f => renderFooterField(f))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 text-end">
        <Button variant="success" onClick={handleSaveTemplate} disabled={!canSaveTemplate}>
          Save Template Changes
        </Button>
      </div>
    </div>
  );

  if (!itemType) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Edit Item Type #{itemType.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Item Type <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={itemType.itemType}
              onChange={(e) => onChange({ ...itemType, itemType: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={itemType.description}
              onChange={(e) => onChange({ ...itemType, description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
            <Form.Label className="me-3 mb-0">Has service checklist template?</Form.Label>
            <Form.Check type="switch">
              <Form.Check.Input
                id="editHasTemplateSwitch"
                checked={hasTemplate}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHasTemplate(checked);
                  if (!checked) {
                    setTemplateJson('');
                    onChange({ ...itemType, checklistTemplate: null });
                    resetTemplateForm();
                  }
                }}
              />
              <Form.Check.Label className="ms-2" htmlFor="editHasTemplateSwitch">
                {hasTemplate ? 'Yes' : 'No'}
              </Form.Check.Label>
            </Form.Check>
          </Form.Group>
          {hasTemplate && (
            <div className="d-flex align-items-center justify-content-between mb-3">
              <small className="text-muted ms-2">
                {templateJson ? 'Template set' : 'No template yet'}
              </small>
            </div>
          )}
          {hasTemplate && renderBuilder()}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide} disabled={isLoading || loading}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSave} disabled={isLoading || loading || (hasTemplate && !templateJson.trim())}>
          {isLoading || loading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditItemTypeModal;